import { apiGet, apiPost, postNdjsonStream } from "./client";
import type { Opportunity, ScanHistory, StreamEvent } from "@sales-ops-amplify/shared";
import type { ExploreResult } from "@/types";

export interface ScanFiltersRequest {
  titlePositive?: string[];
  titleNegative?: string[];
  locationAllow?: string[];
  locationBlock?: string[];
  seniorityBoost?: Record<string, number>;
}

export interface ExploreOffer {
  url: string;
  company: string;
  role?: string;
  dealName?: string;
  location?: string;
  compensation?: string;
  source?: string;
}

export interface FreeScanResponse {
  results: ExploreResult[];
  count: number;
  message?: string;
}

export interface AddOffersResponse {
  added: Opportunity[];
  skipped: number;
  message?: string;
}

export type AiSearchEventCallback = (event: StreamEvent) => void;

/** Trigger a free ATS portal scan with optional filter overrides. */
export async function runFreeScan(
  filters?: ScanFiltersRequest,
): Promise<FreeScanResponse> {
  return apiPost<FreeScanResponse>("/api/discovery", filters ?? {});
}

/** Stream AI-powered job search results. */
export async function runAiSearch(
  query: string,
  onEvent: AiSearchEventCallback,
  signal?: AbortSignal,
): Promise<{ tokens: number; costUsd: number | null } | null> {
  let summary: { tokens: number; costUsd: number | null } | null = null;

  await postNdjsonStream(
    "/api/discovery/ai",
    { query },
    (raw) => {
      const event = raw as StreamEvent;
      if (event.type === "done") {
        summary = { tokens: event.tokens, costUsd: event.costUsd ?? null };
      }
      onEvent(event);
    },
    signal,
  );

  return summary;
}

/** Add discovered offers to the jobs inbox. */
export async function addDiscoveredOffers(
  offers: ExploreOffer[],
  source?: string,
): Promise<AddOffersResponse> {
  return apiPost<AddOffersResponse>("/api/discovery/add", { offers, source });
}

/** Get recent scan history. */
export async function getScanHistory(params?: {
  limit?: number;
  offset?: number;
}): Promise<{
  history: ScanHistory[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}> {
  return apiGet("/api/discovery/history", params);
}
